import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MegaphoneIcon, XMarkIcon, SpeakerWaveIcon, SpeakerXMarkIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { pollingService as websocketService } from '../../services/pollingService';
import { adminService } from '../../services/admin';
import { useAuth } from '../../hooks/useAuth';
import { formatDateTime, getRemainingTime, formatTimeAgo, getCurrentTime } from '../../utils/timezone';

// Create a beep sound using Web Audio API
const createBeepSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.00001, audioContext.currentTime + 0.5);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
  } catch (e) {
    console.log('Web Audio API not supported');
  }
};

const BurnerWidget = () => {
  const { isAuthenticated, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const lastMessageIdRef = useRef(null);
  const userInteractedRef = useRef(false);
  const timerRef = useRef(null);

  // Load initial messages
  const loadMessages = async () => {
    if (!isAuthenticated) return;
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await adminService.getBurnerMessages();
      
      let messagesArray = [];
      if (response?.data?.messages && Array.isArray(response.data.messages)) {
        messagesArray = response.data.messages;
      } else if (response?.messages && Array.isArray(response.messages)) {
        messagesArray = response.messages;
      }
      
      const now = new Date();
      const activeMessages = messagesArray.filter(msg => {
        const expiresAt = new Date(msg.expires_at);
        return msg.is_active === 1 && expiresAt > now;
      });
      
      activeMessages.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setMessages(activeMessages);
      
      if (activeMessages.length > 0) {
        setIsVisible(true);
      }
      
      if (activeMessages.length > 0 && activeMessages[0].id) {
        lastMessageIdRef.current = activeMessages[0].id;
      }
    } catch (error) {
      console.error('Failed to load burner messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user interaction to enable audio
  useEffect(() => {
    const handleUserInteraction = () => {
      userInteractedRef.current = true;
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
    
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    
    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Update timers every minute
  useEffect(() => {
    if (!isExpanded || messages.length === 0) return;
    
    const interval = setInterval(() => {
      setMessages(prev => [...prev]);
    }, 60000);
    
    return () => clearInterval(interval);
  }, [isExpanded, messages.length]);

  // Subscribe to WebSocket for new messages
  useEffect(() => {
    if (!isAuthenticated) return;
    
    loadMessages();
    
    const handleNewMessage = (message) => {
      const expiresAt = new Date(message.expires_at);
      const now = new Date();
      
      if (expiresAt > now && message.is_active !== 0) {
        setMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          const newMessages = [message, ...prev].slice(0, 10);
          newMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          return newMessages;
        });
        
        setIsVisible(true);
        setHasNewMessage(true);
        
        if (!isMuted && userInteractedRef.current) {
          createBeepSound();
        }
        
        if (!isExpanded) {
          setIsExpanded(true);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            setIsExpanded(false);
            setHasNewMessage(false);
          }, 8000);
        }
      }
    };

    const handleMessageRemoved = (data) => {
      setMessages(prev => prev.filter(msg => msg.id !== data.message_id));
      
      if (messages.length <= 1) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          setIsExpanded(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    };

    const updateConnectionStatus = () => {
      setIsConnected(websocketService.isConnected);
    };

    websocketService.on('new_burner_message', handleNewMessage);
    websocketService.on('burner_message_removed', handleMessageRemoved);
    
    if (websocketService.isConnected) {
      websocketService.emit('subscribe_burner');
    }
    
    const interval = setInterval(updateConnectionStatus, 3000);
    const refreshInterval = setInterval(loadMessages, 30000);

    return () => {
      websocketService.off('new_burner_message', handleNewMessage);
      websocketService.off('burner_message_removed', handleMessageRemoved);
      websocketService.emit('unsubscribe_burner');
      clearInterval(interval);
      clearInterval(refreshInterval);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, isExpanded, isMuted]);

  const dismissMessage = (messageId) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
    if (messages.length === 1) {
      setHasNewMessage(false);
    }
  };

  const dismissAllMessages = () => {
    setMessages([]);
    setHasNewMessage(false);
    const timer = setTimeout(() => {
      setIsVisible(false);
      setIsExpanded(false);
    }, 1000);
    return () => clearTimeout(timer);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setHasNewMessage(false);
    }
  };

  const showWidget = () => {
    setIsVisible(true);
    if (messages.length > 0) {
      setIsExpanded(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 10000);
    }
  };

  useEffect(() => {
    if (!isExpanded && messages.length === 0) {
      const timer = setTimeout(() => {
        if (messages.length === 0) {
          setIsVisible(false);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, messages.length]);

  if (!isAuthenticated) return null;

  // Completely hidden when no messages
  if (!isVisible && !isExpanded && messages.length === 0) {
    return null;
  }
  
  // Minimized button (floating on top of everything)
  if (!isExpanded && messages.length > 0) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        onClick={showWidget}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 999999,
        }}
        className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full p-4 shadow-2xl hover:shadow-xl transition-all duration-300 relative"
      >
        <MegaphoneIcon className="h-7 w-7" />
        {hasNewMessage && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse border-2 border-white">
            {messages.length}
          </span>
        )}
        {!hasNewMessage && messages.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-primary-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center border-2 border-white">
            {messages.length}
          </span>
        )}
      </motion.button>
    );
  }

  // Expanded widget - ALWAYS visible on top without scrolling
  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', damping: 20 }}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 999999,
          width: '380px',
          maxWidth: 'calc(100vw - 40px)',
        }}
        className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MegaphoneIcon className="h-5 w-5 text-white animate-pulse" />
            <h3 className="text-white font-bold">LIVE ANNOUNCEMENTS</h3>
            {messages.length > 0 && (
              <span className="bg-white text-red-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {messages.length}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="text-white hover:text-gray-200 transition p-1 rounded hover:bg-white/20"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <SpeakerXMarkIcon className="h-4 w-4" /> : <SpeakerWaveIcon className="h-4 w-4" />}
            </button>
            <button
              onClick={toggleExpand}
              className="text-white hover:text-gray-200 transition p-1 rounded hover:bg-white/20 text-lg font-bold"
            >
              ×
            </button>
          </div>
        </div>


        {/* Messages List */}
        <div className="max-h-80 overflow-y-auto">
          {isLoading && messages.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-600 border-t-transparent mx-auto mb-3"></div>
              <p className="text-sm">Loading announcements...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400">
              <MegaphoneIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No active announcements</p>
              <p className="text-xs mt-1">New announcements will appear here</p>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {messages.map((message, index) => {
                const remainingTime = getRemainingTime(message.expires_at);
                const isExpiringSoon = remainingTime.includes('minute') && parseInt(remainingTime) < 15;
                
                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className="px-4 py-3 hover:bg-gray-50 transition group relative border-b border-gray-100"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-6">
                        <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                          <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                            📢 {message.created_by_username || message.first_name || 'Admin'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatTimeAgo(message.created_at)}
                          </span>
                          {remainingTime !== 'Expired' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              isExpiringSoon ? 'bg-orange-100 text-orange-700 animate-pulse' : 'bg-green-100 text-green-700'
                            }`}>
                              {remainingTime}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-800 text-sm leading-relaxed break-words font-medium">
                          {message.message}
                        </p>
                      
                      </div>
                      <button
                        onClick={() => dismissMessage(message.id)}
                        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
          
          {messages.length > 1 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
              <button
                onClick={dismissAllMessages}
                className="text-xs text-gray-500 hover:text-gray-700 w-full text-center py-1 hover:bg-gray-100 rounded transition"
              >
                Dismiss all announcements
              </button>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}></div>
            {isConnected ? 'Live' : 'Connecting...'}
          </span>
          <button
            onClick={loadMessages}
            disabled={isLoading}
            className="hover:text-red-600 transition flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default BurnerWidget;